import { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Breadcrumbs,
  Link,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  alpha,
} from '@mui/material';
import {
  Search,
  ArrowBack,
  Home,
  SupportAgent,
  Send,
  CheckCircle,
  PlayArrow,
  Category,
  Share,
  DataObject,
  Keyboard,
  Image as ImageIcon,
  StickyNote2,
  Polyline,
  Checklist,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/contexts/AuthContext';
import { brandColors } from '@/theme/colors';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Help topics content in Markdown format
const helpTopics: Record<string, { title: string; category: string; content: string }> = {
  'getting-started': {
    title: 'Getting Started',
    category: 'Basics',
    content: `
# Getting Started with ThinkSpace

ThinkSpace is a collaborative visual thinking tool that helps you organize ideas, create diagrams, and collaborate with others.

## Creating Your First ThinkSpace

1. Click the **"New Space"** button on the dashboard
2. Enter a name and optional description
3. Click **"Create"** to open your new space

## Adding Elements

Once in your ThinkSpace, use the toolbar at the bottom to add elements:

- **Sticky Notes**: Click the note icon to add colorful sticky notes
- **Text**: Add standalone text elements
- **Shapes**: Draw boxes, ellipses, and diamonds
- **Lines & Connectors**: Connect elements with lines or angular connectors
- **Images**: Upload and insert images from your library

## Navigation

- **Zoom**: Use the +/- buttons or scroll wheel to zoom in/out
- **Pan**: Click and drag on empty space to move around
- **Fit to Screen**: Click the fit button to reset the view
    `,
  },
  'sticky-notes': {
    title: 'Working with Sticky Notes',
    category: 'Elements',
    content: `
# Working with Sticky Notes

Sticky notes are the primary way to capture ideas in ThinkSpace.

## Creating a Sticky Note

1. Click the sticky note icon in the toolbar
2. Click anywhere on the canvas to place the note
3. The note appears with a default title you can edit

## Editing Sticky Notes

- **Click the title** to edit it directly
- **Click the content area** to add or edit text
- **Right-click or use the menu** to access properties:
  - Change the color (yellow, pink, blue, green, orange, purple)
  - Bring to front or send to back
  - Delete the note

## Moving and Resizing

- **Drag** the note to move it
- Notes have a fixed size for consistency

## Tips

- Use different colors to categorize your ideas
- Keep titles short and descriptive
- Add detailed notes in the content area
    `,
  },
  'shapes': {
    title: 'Drawing Shapes',
    category: 'Elements',
    content: `
# Drawing Shapes

ThinkSpace provides several shape tools for creating diagrams and visual structures.

## Available Shapes

- **Box/Rectangle**: Great for flowcharts, org charts, and containers
- **Ellipse/Circle**: Ideal for process diagrams and emphasis
- **Diamond**: Perfect for decision points in flowcharts
- **Line**: Simple connecting lines

## Drawing a Shape

1. Select the shape tool from the toolbar
2. Click and drag on the canvas to draw
3. Release to complete the shape

## Customizing Shapes

Select a shape and use the menu to:

- **Fill Color**: Add a background color or leave transparent
- **Stroke Color**: Change the outline color
- **Stroke Width**: Adjust line thickness
- **Border Radius** (boxes only): Round the corners

## Z-Index (Layering)

Use "Bring to Front" and "Send to Back" to control which elements appear on top.
    `,
  },
  'connectors': {
    title: 'Using Connectors',
    category: 'Elements',
    content: `
# Using Connectors

Connectors create angular, Visio-style connections between elements.

## Creating a Connector

1. Select the connector tool (arrow icon) from the toolbar
2. Click and drag from start point to end point
3. The connector automatically routes with 90-degree angles

## Connector Features

- **Angular Routing**: Connectors use only horizontal and vertical segments
- **Smart Routing**: Paths are calculated to avoid overlapping
- **Endpoint Circles**: Selected connectors show handles at each end

## Customizing Connectors

Use the properties menu to adjust:

- **Stroke Color**: Change the line color
- **Stroke Width**: Adjust line thickness

## Connection Points

Elements have 8 connection points:
- 4 cardinal (top, right, bottom, left)
- 4 corners (top-left, top-right, bottom-left, bottom-right)
    `,
  },
  'images': {
    title: 'Working with Images',
    category: 'Elements',
    content: `
# Working with Images

Add images to your ThinkSpace to enhance your visual thinking.

## Image Library

Each ThinkSpace has its own image library where uploaded images are stored.

### Uploading Images

1. Click the image icon in the toolbar
2. Go to the **Upload** tab
3. Click **Choose File** and select an image
4. Supported formats: JPG, PNG, GIF, WebP (max 5MB)

### Using Library Images

1. Open the image library
2. Browse thumbnails in the **Library** tab
3. Click an image to select it
4. Click **Insert Image** to add it to the canvas

## Customizing Images

Select an image and use the properties menu to adjust:

- **Opacity**: Make images semi-transparent
- **Border Radius**: Round the corners
- **Size**: Drag the corner handle to resize (maintains aspect ratio)

## Tips

- Images are stored per-ThinkSpace
- Shared users can view images but only editors can upload
- Delete images from the library to free up storage
    `,
  },
  'sharing': {
    title: 'Sharing & Collaboration',
    category: 'Collaboration',
    content: `
# Sharing & Collaboration

ThinkSpace makes it easy to collaborate with others.

## Sharing a ThinkSpace

1. Open the ThinkSpace you want to share
2. Click the **Share** button in the toolbar
3. Enter the email address of the person to invite
4. Choose their permission level:
   - **Can View**: Read-only access
   - **Can Edit**: Full editing capabilities
5. Click **Invite**

## Managing Shared Users

In the Share dialog, you can:

- See who has access to your ThinkSpace
- Change permission levels
- Remove users

## Accepting Invitations

When someone shares a ThinkSpace with you:

1. You'll see it in the **Pending** tab on your dashboard
2. Click **Accept** to add it to your spaces
3. Click **Decline** to reject the invitation

## Permissions

| Permission | View | Edit | Delete | Share |
|------------|------|------|--------|-------|
| Owner      | Yes  | Yes  | Yes    | Yes   |
| Can Edit   | Yes  | Yes  | No     | No    |
| Can View   | Yes  | No   | No     | No    |
    `,
  },
  'export-import': {
    title: 'Export & Import',
    category: 'Data',
    content: `
# Export & Import

Save and share your work in various formats.

## Export Options

Click the **Export** button to see available formats:

### ThinkSpace Format (.tspace)
- Native JSON format that preserves all data
- Can be imported back into ThinkSpace
- Best for backup and sharing between ThinkSpace users

### PNG Image (.png)
- Raster image format
- Good for presentations and documents
- Exports at 2x resolution for quality

### SVG Vector (.svg)
- Scalable vector format
- Perfect for printing and further editing
- Preserves shapes and text as vectors

## Importing

1. Click the **Import** button
2. Select a **.tspace** file
3. Elements are merged into your current ThinkSpace
4. Imported elements are offset slightly to avoid overlap

## Tips

- Export regularly as backup
- Use PNG for quick sharing
- Use SVG for high-quality prints
- Use .tspace for moving between accounts
    `,
  },
  'keyboard-shortcuts': {
    title: 'Keyboard Shortcuts',
    category: 'Reference',
    content: `
# Keyboard Shortcuts

Speed up your workflow with keyboard shortcuts.

## General

| Shortcut | Action |
|----------|--------|
| Delete/Backspace | Delete selected element |
| Escape | Deselect / Cancel operation |

## Navigation

| Shortcut | Action |
|----------|--------|
| Scroll wheel | Zoom in/out |
| Click + Drag | Pan canvas |

## Elements

| Shortcut | Action |
|----------|--------|
| Click | Select element |
| Click + Drag | Move element |
| Double-click | Edit text/content |

## Tips

- Most actions are also available via right-click menu
- Use the toolbar for quick access to all tools
- Touch gestures work on mobile devices
    `,
  },
  'todo-getting-started': {
    title: 'Getting Started with ToDo Lists',
    category: 'ToDo Lists',
    content: `
# Getting Started with ToDo Lists

ThinkSpace includes simple, shareable ToDo lists to help you track tasks alongside your visual boards.

## Creating a ToDo List

1. Go to your dashboard
2. Click the **"ToDo Lists"** toggle button
3. Click **"New List"** to create a new list
4. Enter a name and optional description
5. Click **"Create"** to open your new list

## Switching Between ThinkSpaces and ToDo Lists

Use the toggle buttons at the top of your dashboard to switch between:
- **ThinkSpaces**: Your visual whiteboards
- **ToDo Lists**: Your task lists

## ToDo List Dashboard

Your ToDo lists show:
- List name and description
- Progress bar showing completion percentage
- Number of completed vs total tasks
- Shared indicator if the list is shared
    `,
  },
  'todo-managing-tasks': {
    title: 'Managing Tasks',
    category: 'ToDo Lists',
    content: `
# Managing Tasks

Organize your tasks efficiently with ThinkSpace's ToDo features.

## Adding Tasks

1. Open a ToDo list
2. Type your task in the "Add a new task..." field
3. Optionally select a priority (Low, Medium, High)
4. Press Enter or click **Add**

## Task Features

Each task can have:
- **Title**: The main task text
- **Description**: Additional notes (expand to edit)
- **Priority**: Low (blue), Medium (orange), or High (red)
- **Due Date**: Optional deadline (shown as a chip)
- **Subtasks**: Break down complex tasks into steps

## Completing Tasks

- Click the checkbox to mark a task complete
- Completed tasks show a strikethrough
- If a task has subtasks, it auto-completes when all subtasks are done

## Editing Tasks

- Click on a task title to edit it inline
- Click the expand icon to view/edit description and subtasks
- Add subtasks in the expanded view

## Deleting Tasks

- Click the delete (trash) icon to remove a task
- Deleting a task also removes all its subtasks

## Drag and Drop Ordering

- Drag tasks using the handle on the left
- Drop to reorder your tasks
- Order is saved automatically
    `,
  },
  'todo-sharing': {
    title: 'Sharing ToDo Lists',
    category: 'ToDo Lists',
    content: `
# Sharing ToDo Lists

Collaborate on tasks by sharing your ToDo lists with others.

## Sharing a ToDo List

1. Open the ToDo list you want to share
2. Click the **Share** button
3. Enter the email address of the person to invite
4. Choose their permission level:
   - **Can View**: See tasks but cannot edit
   - **Can Edit**: Full editing capabilities
5. Click **Invite**

## Managing Shared Users

In the Share dialog, you can:
- See who has access to your list
- Change permission levels using the dropdown
- Remove users by clicking the delete icon

## Accepting Invitations

When someone shares a ToDo list with you:
1. Go to your dashboard and switch to **ToDo Lists**
2. Check the **Pending** tab
3. Click **Accept** to add it to your lists
4. Click **Decline** to reject the invitation

## Permissions

| Permission | View Tasks | Edit Tasks | Delete List | Share |
|------------|-----------|-----------|-------------|-------|
| Owner      | Yes       | Yes       | Yes         | Yes   |
| Can Edit   | Yes       | Yes       | No          | No    |
| Can View   | Yes       | No        | No          | No    |

## Tips

- Shared lists show a "Shared" badge on the dashboard
- You can see how many people a list is shared with
- View-only users can see but not modify tasks
    `,
  },
};

// Category styling configuration
const categoryConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  Basics: {
    icon: <PlayArrow />,
    color: brandColors.purple,
    bgColor: alpha(brandColors.purple, 0.1),
  },
  Elements: {
    icon: <Category />,
    color: brandColors.pink,
    bgColor: alpha(brandColors.pink, 0.1),
  },
  Collaboration: {
    icon: <Share />,
    color: brandColors.accent.green,
    bgColor: alpha(brandColors.accent.green, 0.1),
  },
  Data: {
    icon: <DataObject />,
    color: brandColors.yellow,
    bgColor: alpha(brandColors.yellow, 0.15),
  },
  Reference: {
    icon: <Keyboard />,
    color: brandColors.charcoal,
    bgColor: alpha(brandColors.charcoal, 0.1),
  },
  'ToDo Lists': {
    icon: <Checklist />,
    color: brandColors.purpleDeep,
    bgColor: alpha(brandColors.purpleDeep, 0.1),
  },
};

// Topic-specific icons
const topicIcons: Record<string, React.ReactNode> = {
  'getting-started': <PlayArrow />,
  'sticky-notes': <StickyNote2 />,
  shapes: <Category />,
  connectors: <Polyline />,
  images: <ImageIcon />,
  sharing: <Share />,
  'export-import': <DataObject />,
  'keyboard-shortcuts': <Keyboard />,
  'todo-getting-started': <Checklist />,
  'todo-managing-tasks': <Checklist />,
  'todo-sharing': <Share />,
};

export const HelpPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);
  const [ticketError, setTicketError] = useState('');
  const { user } = useAuth();

  const selectedTopic = searchParams.get('topic');

  // Filter topics based on search query
  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return Object.entries(helpTopics);

    const query = searchQuery.toLowerCase();
    return Object.entries(helpTopics).filter(([, topic]) =>
      topic.title.toLowerCase().includes(query) ||
      topic.content.toLowerCase().includes(query) ||
      topic.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group topics by category
  const topicsByCategory = useMemo(() => {
    const grouped: Record<string, Array<[string, typeof helpTopics[string]]>> = {};
    for (const [key, topic] of filteredTopics) {
      if (!grouped[topic.category]) {
        grouped[topic.category] = [];
      }
      grouped[topic.category].push([key, topic]);
    }
    return grouped;
  }, [filteredTopics]);

  const currentTopic = selectedTopic ? helpTopics[selectedTopic] : null;

  const handleSubmitTicket = async () => {
    if (!user || !ticketSubject.trim() || !ticketMessage.trim()) return;

    setTicketSubmitting(true);
    setTicketError('');

    try {
      // Send support ticket via Firebase callable function
      const functions = getFunctions();
      const sendSupportTicket = httpsCallable(functions, 'sendSupportTicket');
      await sendSupportTicket({
        userEmail: user.email,
        userName: user.displayName || 'User',
        userId: user.uid,
        subject: ticketSubject,
        message: ticketMessage,
      });

      setTicketSuccess(true);
      setTicketDialogOpen(false);
      setTicketSubject('');
      setTicketMessage('');
    } catch {
      setTicketError('Failed to submit support ticket. Please try again or email us directly at help@mythinkspace.app');
    } finally {
      setTicketSubmitting(false);
    }
  };

  return (
    <AppLayout title="Help">
      {/* Colorful Hero Header */}
      <Box
        sx={{
          background: brandColors.gradient,
          py: { xs: 4, md: 6 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 40%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}
            >
              Help Center
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              ml: 7,
              fontWeight: 400,
            }}
          >
            Find answers, learn features, and get support
          </Typography>
          {user && (
            <Button
              variant="contained"
              startIcon={<SupportAgent />}
              onClick={() => setTicketDialogOpen(true)}
              sx={{
                ml: 7,
                mt: 2,
                bgcolor: 'white',
                color: brandColors.purple,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Contact Support
            </Button>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => setSearchParams({})}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Home fontSize="small" />
            Help
          </Link>
          {currentTopic && (
            <>
              <Chip
                label={currentTopic.category}
                size="small"
                sx={{
                  bgcolor: categoryConfig[currentTopic.category]?.bgColor,
                  color: categoryConfig[currentTopic.category]?.color,
                  fontWeight: 500,
                }}
              />
              <Typography color="text.primary" fontWeight={500}>{currentTopic.title}</Typography>
            </>
          )}
        </Breadcrumbs>

        {selectedTopic && currentTopic ? (
          // Topic detail view
          <Box>
            <Card
              sx={{
                borderTop: `4px solid ${categoryConfig[currentTopic.category]?.color || brandColors.purple}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                {/* Topic header with icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: categoryConfig[currentTopic.category]?.bgColor,
                      color: categoryConfig[currentTopic.category]?.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {topicIcons[selectedTopic] || categoryConfig[currentTopic.category]?.icon}
                  </Box>
                  <Box>
                    <Chip
                      label={currentTopic.category}
                      size="small"
                      sx={{
                        bgcolor: categoryConfig[currentTopic.category]?.bgColor,
                        color: categoryConfig[currentTopic.category]?.color,
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    />
                    <Typography variant="h5" fontWeight={600}>
                      {currentTopic.title}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    '& h1': { display: 'none' }, // Hide first h1 since we show it above
                    '& h2': {
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      mb: 1.5,
                      mt: 3,
                      color: brandColors.charcoal,
                      borderBottom: `2px solid ${alpha(brandColors.purple, 0.2)}`,
                      pb: 1,
                    },
                    '& h3': {
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      mb: 1,
                      mt: 2,
                      color: brandColors.purpleDeep,
                    },
                    '& p': { mb: 2, lineHeight: 1.7 },
                    '& ul, & ol': { mb: 2, pl: 3 },
                    '& li': { mb: 0.5 },
                    '& code': {
                      backgroundColor: alpha(brandColors.purple, 0.1),
                      color: brandColors.purpleDeep,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontFamily: 'monospace',
                    },
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      mb: 2,
                      borderRadius: 2,
                      overflow: 'hidden',
                    },
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1.5,
                      textAlign: 'left',
                    },
                    '& th': {
                      background: brandColors.gradient,
                      color: 'white',
                      fontWeight: 600,
                    },
                    '& strong': { fontWeight: 600, color: brandColors.charcoal },
                  }}
                >
                  <ReactMarkdown>{currentTopic.content}</ReactMarkdown>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
          // Index view
          <Box>
            {/* Search */}
            <TextField
              fullWidth
              placeholder="Search help topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: brandColors.purple }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'white',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: brandColors.purple,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: brandColors.purple,
                  },
                },
              }}
            />

            {/* Topics by category */}
            {Object.entries(topicsByCategory).map(([category, topics]) => {
              const config = categoryConfig[category] || categoryConfig.Basics;
              return (
                <Box key={category} sx={{ mb: 5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: config.bgColor,
                        color: config.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {config.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.charcoal }}>
                      {category}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {topics.map(([key, topic]) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                        <Card
                          sx={{
                            height: '100%',
                            borderLeft: `4px solid ${config.color}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 8px 24px ${alpha(config.color, 0.2)}`,
                            },
                          }}
                        >
                          <CardActionArea
                            onClick={() => setSearchParams({ topic: key })}
                            sx={{ height: '100%' }}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <Box sx={{ color: config.color }}>
                                  {topicIcons[key] || config.icon}
                                </Box>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {topic.title}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {topic.content.split('\n').find(line => line.trim() && !line.startsWith('#'))?.trim().substring(0, 100)}...
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}

            {filteredTopics.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: alpha(brandColors.purple, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Search sx={{ fontSize: 40, color: brandColors.purple }} />
                </Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No results found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try different search terms
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Support Ticket Dialog */}
        <Dialog
          open={ticketDialogOpen}
          onClose={() => setTicketDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              background: brandColors.gradient,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <SupportAgent />
            Contact Support
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Submit a support ticket and we'll get back to you within 24 hours.
            </Typography>
            {ticketError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ticketError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Subject"
              value={ticketSubject}
              onChange={(e) => setTicketSubject(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Brief description of your issue"
            />
            <TextField
              fullWidth
              label="Message"
              value={ticketMessage}
              onChange={(e) => setTicketMessage(e.target.value)}
              multiline
              rows={5}
              placeholder="Please describe your issue or question in detail..."
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setTicketDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleSubmitTicket}
              disabled={ticketSubmitting || !ticketSubject.trim() || !ticketMessage.trim()}
              sx={{
                background: brandColors.gradient,
                '&:hover': {
                  background: brandColors.gradient,
                  filter: 'brightness(1.1)',
                },
              }}
            >
              {ticketSubmitting ? 'Sending...' : 'Send Ticket'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={ticketSuccess}
          autoHideDuration={6000}
          onClose={() => setTicketSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setTicketSuccess(false)}
            severity="success"
            sx={{
              bgcolor: brandColors.accent.green,
              color: 'white',
              '& .MuiAlert-icon': { color: 'white' },
            }}
            icon={<CheckCircle />}
          >
            Support ticket submitted successfully! We'll respond within 24 hours.
          </Alert>
        </Snackbar>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            &copy; {new Date().getFullYear()}{' '}
            <Link
              href="https://www.advancer.co.nz"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              sx={{ '&:hover': { color: brandColors.purple } }}
            >
              Advancer Limited
            </Link>
            {' • '}
            <Link
              href="mailto:info@mythinkspace.app"
              color="inherit"
              sx={{ '&:hover': { color: brandColors.purple } }}
            >
              info@mythinkspace.app
            </Link>
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
            ThinkSpace is operated by Advancer Limited (NZBN: 9429041814352)
          </Typography>
        </Box>
      </Container>
    </AppLayout>
  );
};
